import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApiNa} from '@di-shared/api/di';
import {ERROR, UNIT} from '@di-shared/const';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface SafeDrivingSectionLNProps {
  vin: string;
}

function SafeDrivingSectionLN(props: SafeDrivingSectionLNProps) {
  return (
    <ErrorBoundary fallback={<SafeDrivingSectionLNError {...props} />}>
      <Suspense fallback={<SafeDrivingSectionLNLoading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: SafeDrivingSectionLNProps) {
  const {t} = useInferableTranslation();

  const {data: ubiData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.safeDriving.latest), vin],
    queryFn: () => diApiNa.safeDriving.latest({vin}),
    select: ({data}) => {
      return {
        ...data,
        latestScoreInfo: {
          ...data.latestScoreInfo,
          lastUpdatedDate: parseDateWithFormat(data.latestScoreInfo.lastUpdatedDate),
        },
      };
    },
  });

  return (
    <div className="section section_safe_score type_eu">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
          aria-label={t('ctn_home_Learnmoreabout')}
        />
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">{ubiData.latestScoreInfo.drivingSafetyScore}</span>
            <span className="safe_score_unit">{t('ctn_home_dss_points')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_dss_LN_points_$score', {
                score:
                  ubiData.latestScoreInfo.previousScoreDelta === 0
                    ? ubiData.latestScoreInfo.previousScoreDelta
                    : ubiData.latestScoreInfo.previousScoreDelta > 0
                      ? `+${ubiData.latestScoreInfo.previousScoreDelta}`
                      : `-${ubiData.latestScoreInfo.previousScoreDelta}`,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_not')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ubiData.latestScoreInfo.rank}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">
              {ubiData.latestScoreInfo.drivingSafetyScore === 100
                ? t('ctn_dss_LN_driving_safety_score')
                : t('ctn_dss_LN_points_$score', {score: ubiData.latestScoreInfo.nextRank})}
            </span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">
                {ubiData.latestScoreInfo.drivingSafetyScore === 100
                  ? t('msg_dss_LN_you_have_outdone_yourself')
                  : ubiData.latestScoreInfo.drivingSafetyScore === 0
                    ? t('msg_dss_LN_keep_higher_score')
                    : t('ctn_dss_LN_points_$score', {score: ubiData.latestScoreInfo.remainingPointToNextRank})}
              </span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_dss_LN_guidance_driving_annual_mileage_title')}
                </span>
                <span className="score_summary_value">
                  {/* // [2026-01-09:dooking] FIXME: 안전운전점수 다국어 imsi_home_p*/}
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.estimatedAnnualMileage})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_warning_break_oil"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_hard_braking_events')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.hardBraking})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_avgspeed"
                aria-hidden="true"
              />
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_average_trip_speed')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.averageTripSpeed})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_maxspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_highway_speeding_events')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.maximumHighSpeed})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_time"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_time_of_day_driving')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.drivingTimeOfDay})}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingSectionLNLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="section section_safe_score type_eu skeleton">
      <div className="section_container">
        <div className="section_container_full_btn ">
          <span className="blind">{t('ctn_home_Learnmoreabout')}</span>
        </div>
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">100</span>
            <span className="safe_score_unit">{t('ctn_home_dss_title')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_dss_LN_points_$score', {
                score: 0,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_not')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{10}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">100%</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{10}</span>
              <span className="statistics_info_unit">P</span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul
            className="score_summary_list"
            style={{margin: 0}}
          >
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_dss_LN_guidance_driving_annual_mileage_title')}
                </span>
                <span className="score_summary_value">{t('imsi_home_p', {n: 10})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingSectionLNError({vin}: SafeDrivingSectionLNProps) {
  const {t} = useInferableTranslation();

  return (
    <div className="section section_safe_score type_eu">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
        >
          <span className="blind">{t('ctn_home_Learnmoreabout')}</span>
        </LinkTo>
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_unit">{t('ctn_home_dss_title')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_dss_LN_points_$score', {
                score: ERROR.DASH,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_not')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_dss_LN_driving_safety_score')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{t('ctn_dss_LN_points_$score', {score: ERROR.DASH})}</span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_dss_LN_guidance_driving_annual_mileage_title')}
                </span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_warning_break_oil"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_hard_braking_events')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_avgspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_average_trip_speed')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_maxspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_highway_speeding_events')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_time"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_LN_time_of_day_driving')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

SafeDrivingSectionLN.Loading = () => <SafeDrivingSectionLNLoading />;
export default SafeDrivingSectionLN;
